import type { ApplianceLicenceStatus, LicenceCatalogFeature, ResellerLicence } from "@xop-any-time/shared";
import {
  applianceLicenceStatus,
  daysUntilExpiry,
  formatLicenceDate,
  licenceFeatureGroups,
  licenceFileName,
  licenceRenewalNext,
} from "@xop-any-time/shared";
import { Box, Chip, Link as MuiLink, Typography } from "@mui/material";
import type { JSX } from "react";
import { Link } from "react-router-dom";

import { downloadLicenceFile } from "../../../lib/crmApi.js";
import { saveBlob } from "../../../lib/download.js";
import { EMPTY } from "../../../lib/format.js";

/**
 * The small pieces every licensing surface shares — the status chip, the
 * expiry phrasing, what comes next, and taking the file.
 *
 * Together in one file because they are only ever used together: the licences
 * list, the builder, the customer card and the overview each render a chip and
 * an expiry, and splitting them across three files would obscure that they ARE
 * shared.
 */

const CHIP: Record<
  ApplianceLicenceStatus,
  { label: string; color: "success" | "warning" | "error" | "default" }
> = {
  active: { label: "Active", color: "success" },
  expiring: { label: "Expiring", color: "warning" },
  expired: { label: "Expired", color: "error" },
  // "Perpetual", not "No end date" — and unlike the old projection, this is now
  // a fact rather than a guess. A licence row's `expires_on` is either a date or
  // deliberately NULL, and NULL is what makes the file omit `expires:` and the
  // box never lapse.
  perpetual: { label: "Perpetual", color: "default" },
};

export function LicenceStatusChip({
  licence,
}: {
  licence: Pick<ResellerLicence, "expiresOn">;
}): JSX.Element {
  // The command center's presentation (`licence-chips.tsx`): a coloured
  // term is FILLED, perpetual is outlined — the same licence reads the same
  // in both portals.
  const { label, color } = CHIP[applianceLicenceStatus(licence)];
  return <Chip size="small" label={label} color={color} variant={color === "default" ? "outlined" : "filled"} />;
}

/** The TYPE — Trial or Full — as a light chip, wherever a licence is listed. */
export function LicenceKindChip({ licence }: { licence: Pick<ResellerLicence, "kind"> }): JSX.Element {
  return licence.kind === "trial" ? (
    <Chip size="small" color="info" variant="outlined" label="Trial" data-testid="licence-kind" />
  ) : (
    <Chip size="small" variant="outlined" label="Full" data-testid="licence-kind" />
  );
}

/**
 * The WORKFLOW — the third axis. A TRIAL licence needs nobody; a FULL licence
 * always waits for XOP's executives, and the file does not exist until one
 * of them approves it. "Needs approval" is the state BEFORE anyone has asked.
 *
 * Trial-ness is the licence's stored `kind` (2026-09-15), not a reading of its
 * end date: a full licence with a short term still waits for approval.
 */
export function LicenceWorkflowChip({
  licence,
}: {
  licence: Pick<ResellerLicence, "kind" | "approval">;
}): JSX.Element {
  // The command center's words and colours, verbatim (`licence-chips.tsx`).
  const { status } = licence.approval;
  if (status === "pending") return <Chip size="small" color="warning" variant="outlined" label="Awaiting approval" />;
  if (status === "approved") return <Chip size="small" color="success" variant="outlined" label="Approved" />;
  if (status === "rejected") return <Chip size="small" color="error" label="Rejected" />;
  if (licence.kind === "trial") {
    return <Chip size="small" color="info" variant="outlined" label="Trial" />;
  }
  // A WARNING badge, not a neutral tag: this licence cannot produce a file
  // until someone acts.
  return <Chip size="small" color="warning" label="Needs approval" />;
}

/**
 * How long is left, in words.
 *
 * Days rather than a date, because the question this answers on every surface
 * is "do I need to do something about this", and "in 12 days" answers it
 * without arithmetic. The date itself is always in the adjacent column.
 */
export function expiryPhrase(licence: Pick<ResellerLicence, "expiresOn">): string {
  const days = daysUntilExpiry(licence.expiresOn);
  if (days === null) return EMPTY;
  if (days < 0) return `${Math.abs(days)} ${Math.abs(days) === 1 ? "day" : "days"} ago`;
  if (days === 0) return "Today";
  return `in ${days} ${days === 1 ? "day" : "days"}`;
}

/**
 * The date AND the distance in one sentence — "Expires Oct 15, 2026 · in 30
 * days", "Expired Oct 1, 2026 · 3 days ago", "Perpetual" — for a surface that
 * has no adjacent date column (the customer card).
 */
export function expirySentence(licence: Pick<ResellerLicence, "expiresOn">): string {
  const days = daysUntilExpiry(licence.expiresOn);
  if (days === null) return "Perpetual";
  const date = formatLicenceDate(licence.expiresOn);
  if (days < 0) return `Expired ${date} · ${expiryPhrase(licence)}`;
  return `Expires ${date} · ${days === 0 ? "today" : expiryPhrase(licence)}`;
}

/**
 * What comes NEXT for a licence — the successor's state, the open window, or
 * when it opens — as one line under the licence on the customer page. One
 * fact (`licenceRenewalNext`, shared) printed one way; the list prints the
 * same fact as a chip. Nothing to say renders nothing.
 */
export function LicenceRenewalNextLine({
  licence,
}: {
  licence: Pick<ResellerLicence, "id" | "renewal">;
}): JSX.Element | null {
  const next = licenceRenewalNext(licence);
  if (!next) return null;

  const link = (id: number, text: string): JSX.Element => (
    <MuiLink component={Link} to={`/licences/${id}`} underline="hover">
      {text}
    </MuiLink>
  );
  let content: JSX.Element | string;
  switch (next.kind) {
    case "generated":
      content = (
        <>
          Next licence {link(next.successorId, next.serial)} already generated
          {next.validUntil ? ` · valid until ${formatLicenceDate(next.validUntil)}` : " · perpetual"}
        </>
      );
      break;
    case "pending":
      content = <>Renewal {link(next.successorId, next.serial)} waiting for XOP&apos;s approval</>;
      break;
    case "rejected":
      content = <>Renewal {link(next.successorId, next.serial)} not approved by XOP&apos;s executives</>;
      break;
    case "drafted":
      content = <>Renewal {link(next.successorId, next.serial)} drafted, not generated</>;
      break;
    case "renewable":
      content = link(licence.id, "Renew now");
      break;
    case "opens":
      content = `Renewal opens ${formatLicenceDate(next.on)}`;
      break;
  }
  return (
    <Typography
      component="div"
      data-testid="customer-licence-next"
      sx={{ fontSize: 12.5, color: next.kind === "renewable" ? "primary.main" : "text.secondary" }}
    >
      {content}
    </Typography>
  );
}

/** The same fact as a light chip, for the list's Expires column. */
export function LicenceRenewalChip({
  licence,
}: {
  licence: Pick<ResellerLicence, "id" | "renewal">;
}): JSX.Element | null {
  const next = licenceRenewalNext(licence);
  if (!next) return null;
  switch (next.kind) {
    case "generated":
      return <Chip size="small" variant="outlined" color="success" label={`Next: ${next.serial}`} data-testid="licence-renewal-chip" />;
    case "pending":
      return <Chip size="small" variant="outlined" color="warning" label="Renewal awaiting approval" data-testid="licence-renewal-chip" />;
    case "rejected":
      return <Chip size="small" variant="outlined" color="error" label="Renewal not approved" data-testid="licence-renewal-chip" />;
    case "drafted":
      return <Chip size="small" variant="outlined" label="Renewal drafted" data-testid="licence-renewal-chip" />;
    case "renewable":
      return <Chip size="small" variant="outlined" color="primary" label="Renewal ready" data-testid="licence-renewal-chip" />;
    case "opens":
      return <Chip size="small" variant="outlined" label={`Renewal opens ${formatLicenceDate(next.on)}`} data-testid="licence-renewal-chip" />;
  }
}

/**
 * What an appliance may run, in the two sections every feature list in this
 * portal uses — Value Added Services, then Platform Extensions — named, never
 * keyed. A key the catalogue no longer links is shown as a legacy key under
 * Platform Extensions, in the licence's own words.
 */
export function LicenceFeatureSummary({
  licence,
  catalog,
}: {
  licence: Pick<ResellerLicence, "featureKeys" | "features">;
  catalog: readonly LicenceCatalogFeature[];
}): JSX.Element {
  const groups = licenceFeatureGroups(licence, catalog);
  if (groups.length === 0) {
    return (
      <Typography sx={{ fontSize: 12.5, color: "text.secondary" }} data-testid="customer-licence-features">
        No features licensed.
      </Typography>
    );
  }
  return (
    <Box data-testid="customer-licence-features" sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {groups.map((group) => (
        <Typography key={group.section} component="div" sx={{ fontSize: 12.5, color: "text.secondary" }}>
          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
            {group.label}:
          </Box>{" "}
          {group.features.map((feature, index) => (
            <Box component="span" key={feature.key} title={feature.legacy ? `Legacy appliance key ${feature.key}` : undefined}>
              {index > 0 ? ", " : ""}
              {feature.name}
            </Box>
          ))}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * Take the already-issued file.
 *
 * This does NOT issue anything: the bundle was signed when someone pressed
 * Issue, and this is a download of the stored bytes — counted and audited
 * upstream, refusals included. Never call it speculatively.
 */
export async function takeLicenceFile(
  licence: Pick<ResellerLicence, "id" | "customerName" | "currentIssue">,
): Promise<void> {
  const blob = await downloadLicenceFile(licence.id);
  await saveBlob(
    licenceFileName({
      customerName: licence.customerName,
      serial: licence.currentIssue?.serial ?? null,
    }),
    blob,
  );
}
